import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { defineMessages } from "react-intl";
import { TextFieldHelperText } from "../../components/Forms/TextFieldHelperText";
import { validationMessages } from "../../components/Forms/validationMessages";
import { Icon } from "../../components/Icon";
import { generalMessages } from "../../components/messages";
import { useMsg } from "../../components/Msg/Msg";
import { H2 } from "../../components/Typography";
import { useStaticCallback } from "../../hooks/useStaticCallback.hook";
import { useBlockNavigationPopup } from "../Feedback/CreateFeedbackForm";

export const saveableNoteMessages = defineMessages({
  "notes.title": {
    id: "notes.title",
    defaultMessage: "Notes",
  },
});

export const SaveableNote = ({
  // eslint-disable-next-line react-hooks/rules-of-hooks
  title = useMsg({ dict: saveableNoteMessages })("notes.title"),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  placeholder = useMsg({ dict: generalMessages })("general.type-something"),
  query,
  to, // static
  mutation,
  from,
  unsavedRef,
}) => {
  const generalMsg = useMsg({ dict: generalMessages });
  const validationMsg = useMsg({ dict: validationMessages });
  const initializedRef = useRef(false);
  const [note, setNote] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const toStatic = useStaticCallback(to);
  useEffect(() => {
    // isFetching needed - old query data returned from cache, not updated by setQueryData. Save, before query fetch leave page and return
    if (!query.data || query.isFetching || initializedRef.current) return;
    const value = toStatic(query.data);
    const initNote = typeof value !== "string" ? "" : value;
    // console.log("Inited:", { "query.data": query.data, "initializedRef.current": initializedRef.current, initNote: initNote, });
    setNote(initNote);
    setIsInitialized(true);
    initializedRef.current = true;
  }, [query.data, query.isFetching, toStatic]);
  const isSaved = to(query.data) === note;
  useEffect(() => {
    if (unsavedRef && isInitialized) unsavedRef.current = !isSaved;
  }, [isInitialized, isSaved, unsavedRef]);

  const maxLength = 100000;
  const error = note.length > maxLength;

  const handleSave = (e) => {
    e.preventDefault();
    mutation.mutate(from(note));
  };
  useBlockNavigationPopup({
    isActive: !isSaved,
    title: generalMsg("general.unsaved-changes.title"),
    closeLabel: generalMsg("general.unsaved-changes.close"),
    leaveLabel: generalMsg("general.unsaved-changes.leave"),
  });

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <H2 sx={{ mb: 2 }}>{title}</H2>
      <TextField
        sx={{
          flex: 1,
          width: "100%",
          fontSize: "1rem",
        }}
        variant="outlined"
        multiline
        placeholder={placeholder}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        error={error}
        disabled={!isInitialized}
        helperText={
          <TextFieldHelperText
            charCounter={{ value: note.length, max: maxLength }}
            fieldErrorElement={
              !error
                ? null
                : validationMsg("dict.validation.maxLength", {
                    lteLength: maxLength,
                  })
            }
          />
        }
      />

      <CircularProgress
        color="inherit"
        size={20}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          opacity: mutation.isPending ? 0.15 : query.isFetching ? 0.1 : 0,
        }}
      />
      <Button
        variant="contained"
        sx={{ alignSelf: "flex-end", mt: 2 }}
        disabled={error || mutation.isPending || !isInitialized}
        onClick={handleSave}
        loading={mutation.isPending || query.isFetching || !isInitialized}
        loadingPosition="start"
        startIcon={<Icon name={isSaved ? "Check" : "ArrowUpward"} />}
      >
        {!isInitialized
          ? generalMsg("general.loading")
          : isSaved
            ? generalMsg("general.saved")
            : generalMsg("general.save")}
      </Button>
    </Box>
  );
};
